/**
 * "Download film" for both film pages (Film Printing and Film Pre-print).
 *
 * Owns the single-flight guard and the per-film spinner state, runs
 * `downloadFilm` once per click, and turns a film that could not be saved into
 * the page's warning. The ref is the guard because it is read synchronously: an
 * overlapping click for a film already downloading must neither run a second
 * prepare nor clear the first click's spinner.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getUserMessage } from '@/lib/trpc-error-utils';
import { downloadFilm, type FilmPrepLike } from '../utils/film-file';

export interface UseFilmDownloadOptions {
  /** Shows a warning on the page (the scan-feedback `triggerWarning`). */
  onWarn: (title: string, body: string) => void;
  /** The prepare had no film to download; Film Printing refetches its garments. */
  onUnavailable?: () => void;
}

export interface FilmDownload {
  /** Download one film, running `prepare` once. A no-op while that film is already downloading. */
  download: (id: string, prepare: () => Promise<FilmPrepLike>) => Promise<void>;
  /** Drives the film's button spinner. */
  isDownloading: (id: string) => boolean;
  /** Clears every spinner (location change). In-flight downloads still release their own guard. */
  reset: () => void;
}

export function useFilmDownload({ onWarn, onUnavailable }: UseFilmDownloadOptions): FilmDownload {
  const { t } = useTranslation('production');
  const inFlightRef = useRef<Set<string>>(new Set());
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());

  // Latest page callbacks + t, read at use so `download` keeps a stable identity.
  const latestRef = useRef({ onWarn, onUnavailable, t });
  useEffect(() => { latestRef.current = { onWarn, onUnavailable, t }; });

  const download = useCallback(async (id: string, prepare: () => Promise<FilmPrepLike>) => {
    if (inFlightRef.current.has(id)) return;
    inFlightRef.current.add(id);
    setDownloadingIds((s) => new Set(s).add(id));
    const translate = latestRef.current.t;
    const title = translate('filmPrint.downloadFilmFailedTitle');
    try {
      const result = await downloadFilm(id, { prepare });
      if (result === 'unavailable') {
        latestRef.current.onWarn(title, translate('filmPrint.downloadFilmUnavailableBody'));
        latestRef.current.onUnavailable?.();
      } else if (result === 'blocked') {
        latestRef.current.onWarn(title, translate('filmPrint.downloadFilmBlockedBody'));
      }
    } catch (error) {
      latestRef.current.onWarn(title, getUserMessage(error));
    } finally {
      inFlightRef.current.delete(id);
      setDownloadingIds((s) => {
        if (!s.has(id)) return s;
        const next = new Set(s);
        next.delete(id);
        return next;
      });
    }
  }, []);

  const isDownloading = useCallback((id: string) => downloadingIds.has(id), [downloadingIds]);
  const reset = useCallback(() => setDownloadingIds(new Set()), []);

  return { download, isDownloading, reset };
}
